// src/screens/ScanQRScreen.js - Improved version
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { saveQRData } from "../services/localStorageService";

const ScanQRScreen = ({ setQrData }) => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Check if there's an ID parameter in the URL and auto-redirect
    const queryParams = new URLSearchParams(window.location.search);
    const idParam = queryParams.get("id");

    if (idParam) {
      // Auto-redirect to disclaimer with the ID parameter
      navigate(`/disclaimer?id=${idParam}`);
      return;
    }

    // Check if camera is available
    const checkCamera = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(
          (device) => device.kind === "videoinput"
        );
        setHasCamera(cameras.length > 0);

        if (cameras.length > 0) {
          startCamera();
        } else {
          setErrorMessage(
            "No camera detected. Please scan the QR code manually."
          );
        }
      } catch (error) {
        console.error("Error checking camera:", error);
        setErrorMessage(
          "Unable to access camera. Please scan the QR code manually."
        );
        setHasCamera(false);
      }
    };

    checkCamera();

    return () => {
      stopCamera();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setScanning(true);
        scanQRCode();
      }
    } catch (error) {
      console.error("Error starting camera:", error);
      setErrorMessage(
        "Unable to access camera. Please scan the QR code manually."
      );
      setHasCamera(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setScanning(false);
  };

  const scanQRCode = () => {
    if (!scanning) return;

    // Note: In a real app, you would use a QR code scanning library
    // For this example, we'll simulate scanning with a countdown

    // Start countdown for simulated scanning
    let seconds = 5;
    setCountdown(seconds);

    const countdownInterval = setInterval(() => {
      seconds--;
      setCountdown(seconds);

      if (seconds <= 0) {
        clearInterval(countdownInterval);
        simulateSuccessfulScan();
      }
    }, 1000);
  };

  const simulateSuccessfulScan = () => {
    // Create dummy QR data for testing
    const dummyData = {
      eventId: "event123",
      eventName: "Summer Spirits Festival",
      bartenderId: "bartender456",
      bartenderName: "John Smith",
      bartenderCallsign: "Mixer Master",
      promotion:
        "Get a free cocktail with your first purchase! Try our premium spirits selection and experience the difference in quality and taste.",
      venue: "Skyline Lounge",
      featuredBrands: [
        { name: "Premium Vodka", logoUrl: "https://via.placeholder.com/100" },
        { name: "Craft Gin", logoUrl: "https://via.placeholder.com/100" },
        { name: "Aged Whiskey", logoUrl: "https://via.placeholder.com/100" },
      ],
    };

    // Process successful scan
    handleSuccessfulScan(dummyData);
  };

  const handleSuccessfulScan = (data) => {
    stopCamera();
    setQrData(data);
    saveQRData(data);

    // Record the scan in Firestore would happen here in a real app

    toast.success("QR Code scanned successfully!");
    navigate("/promotion");
  };

  const handleManualEntry = (e) => {
    e.preventDefault();

    try {
      // Get the QR code data from URL query params
      const queryParams = new URLSearchParams(window.location.search);
      const qrDataParam = queryParams.get("data");
      const idParam = queryParams.get("id");

      if (idParam) {
        // If there's an ID parameter, navigate to disclaimer with the ID
        navigate(`/disclaimer?id=${idParam}`);
      } else if (qrDataParam) {
        const decodedData = JSON.parse(decodeURIComponent(qrDataParam));
        setQrData(decodedData);
        saveQRData(decodedData);
        navigate("/promotion");
      } else {
        // For testing purposes, use dummy data if no real data is available
        simulateSuccessfulScan();
      }
    } catch (error) {
      console.error("Error processing QR data:", error);
      toast.error("Invalid QR code data. Please try again.");
    }
  };

  return (
    <div className="screen-container">
      <h1 className="screen-title">Scan QR Code</h1>

      {hasCamera ? (
        <div className="qr-scanner-container">
          <div className="qr-preview">
            <video ref={videoRef} autoPlay playsInline muted></video>
            <div className="qr-marker"></div>
            {countdown !== null && (
              <div
                style={{
                  position: "absolute",
                  bottom: "20px",
                  left: "0",
                  right: "0",
                  textAlign: "center",
                  color: "white",
                  fontSize: "18px",
                  fontWeight: "bold",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  padding: "8px",
                }}
              >
                Scanning... {countdown}
              </div>
            )}
          </div>

          <p className="qr-instructions">
            Position the QR code within the square marker to scan.
            {scanning && <span> Scanning in progress...</span>}
          </p>

          <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
        </div>
      ) : (
        <div className="manual-entry-container">
          <div
            style={{
              padding: "20px",
              backgroundColor: "#FFF9C4",
              borderRadius: "8px",
              marginBottom: "20px",
              borderLeft: "4px solid #FBC02D",
            }}
          >
            <p className="error-message" style={{ color: "#F57F17" }}>
              {errorMessage}
            </p>
            <p style={{ marginTop: "10px" }}>
              Click the button below to continue with a sample QR code.
            </p>
          </div>
        </div>
      )}

      <button
        onClick={handleManualEntry}
        className="button"
        style={{ marginTop: "20px" }}
      >
        {hasCamera ? "Continue without scanning" : "Use sample QR code"}
      </button>
    </div>
  );
};

export default ScanQRScreen;
